import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Building2,
  CalendarDays,
  Clock3,
  ExternalLink,
  LoaderCircle,
  Mail,
  MapPin,
  Navigation,
  PackageCheck,
  Phone,
  Plus,
  RefreshCw,
  Route,
  Save,
  Truck,
  X,
} from "lucide-react";
import NavbarUtility from './NavbarUtility';

const API_URL = "https://dashboard.technoparts.it/ws/ritiri.php";
const GOLD = "#ebb51b";

// Tile scure CARTO (Dark Matter): niente API key, già in tema, licenza
// gratuita per uso standard con attribuzione. Nessun worker da configurare
// a differenza di MapLibre, quindi molto più affidabile sotto CRA/Webpack.
const DARK_TILE_URL =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_3fm0_1_4bf9a4b24a8165c128bc57df";

const DARK_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

  
const EMPTY_FORM = {
  codice_fornitore: "",
  ragione_sociale: "",
  nome_sede: "",
  indirizzo: "",
  localita: "",
  provincia: "",
  apertura_mattina_da: "",
  apertura_mattina_a: "",
  apertura_pomeriggio_da: "",
  apertura_pomeriggio_a: "",
  ritiro_mattina_da: "",
  ritiro_mattina_a: "",
  ritiro_pomeriggio_da: "",
  ritiro_pomeriggio_a: "",
  referente: "",
  telefono: "",
  email: "",
  note: "",
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatQuantity = (value) =>
  new Intl.NumberFormat("it-IT", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]/g, "");

const cleanTime = (value) => {
  if (!value) return null;
  const match = String(value).match(/(\d{1,2}):(\d{2})/);
  if (!match) return String(value);
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

const formatRange = (from, to) => {
  const a = cleanTime(from);
  const b = cleanTime(to);
  if (!a && !b) return null;
  if (a && !b) return `dalle ${a}`;
  if (!a && b) return `fino alle ${b}`;
  return `${a} – ${b}`;
};

const formatWindows = (site, prefix) => {
  const morning = formatRange(
    site?.[`${prefix}_mattina_da`],
    site?.[`${prefix}_mattina_a`],
  );
  const afternoon = formatRange(
    site?.[`${prefix}_pomeriggio_da`],
    site?.[`${prefix}_pomeriggio_a`],
  );
  return [morning, afternoon].filter(Boolean).join(" / ") || "—";
};

const formatDbDate = (value) => {
  if (!value) return "—";
  const match = String(value).match(/\d{4}-\d{2}-\d{2}/);
  if (!match) return String(value);

  const date = new Date(`${match[0]}T12:00:00`);
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  });
};

const formatLongDate = (value) => {
  if (!value) return "Oggi";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const makeAddress = (site) =>
  [site?.indirizzo, site?.localita, site?.provincia, "Italia"]
    .filter(Boolean)
    .join(", ");

const mapsUrl = (site) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    makeAddress(site),
  )}`;

const pickupSortValue = (site) => {
  const candidates = [
    site?.ritiro_mattina_da,
    site?.ritiro_pomeriggio_da,
    site?.apertura_mattina_da,
  ];

  for (const value of candidates) {
    const clean = cleanTime(value);
    if (clean) return clean;
  }

  return "99:99";
};

const getSiteKey = (site) =>
  String(
    site?.id_ritiro ||
      [
        site?.codice_fornitore,
        site?.nome_sede,
        site?.indirizzo,
        site?.localita,
      ].join("|"),
  );

const flattenSites = (data) => {
  if (Array.isArray(data?.tutte_sedi)) return data.tutte_sedi;
  if (Array.isArray(data?.all_sedi)) return data.all_sedi;
  if (Array.isArray(data?.sedi)) return data.sedi;

  const map = new Map();

  (data?.fornitori || []).forEach((supplier) => {
    (supplier?.sedi || []).forEach((site) => {
      const key = getSiteKey(site);
      if (!map.has(key)) {
        map.set(key, site);
      }
    });
  });

  return [...map.values()];
};

const findActiveSupplierForSite = (site, suppliers) => {
  const exactSupplier = suppliers.find(
    (supplier) =>
      Number(supplier.codice_fornitore) === Number(site.codice_fornitore),
  );

  if (!exactSupplier) return null;

  if ((exactSupplier.sedi || []).length <= 1) return exactSupplier;

  const siteLocalita = normalize(site.localita);
  const siteAddress = normalize(site.indirizzo);

  const supplierLocalita = normalize(exactSupplier.fornitore_localita);
  const supplierAddress = normalize(exactSupplier.fornitore_indirizzo);

  if (
    (siteLocalita &&
      supplierLocalita &&
      (siteLocalita.includes(supplierLocalita) ||
        supplierLocalita.includes(siteLocalita))) ||
    (siteAddress &&
      supplierAddress &&
      (siteAddress.includes(supplierAddress) ||
        supplierAddress.includes(siteAddress)))
  ) {
    return exactSupplier;
  }

  /*
   * Se il gestionale non permette di distinguere due sedi dello stesso
   * codice fornitore, le consideriamo entrambe possibili.
   */
  return exactSupplier;
};

const geocodeCacheKey = (address) => `ritiri:geo:v3:${normalize(address)}`;

async function geocodeSite(site) {
  const directLat = Number(site?.latitudine ?? site?.lat ?? site?.latitude);
  const directLng = Number(
    site?.longitudine ?? site?.lng ?? site?.lon ?? site?.longitude,
  );

  if (Number.isFinite(directLat) && Number.isFinite(directLng)) {
    return { lat: directLat, lng: directLng };
  }

  const address = makeAddress(site);
  if (!address.trim()) return null;

  const key = geocodeCacheKey(address);

  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Number.isFinite(parsed?.lat) && Number.isFinite(parsed?.lng)) {
        return parsed;
      }
    }
  } catch {
    // localStorage non disponibile: continuiamo senza cache.
  }

  const url =
    "https://nominatim.openstreetmap.org/search?" +
    new URLSearchParams({
      format: "jsonv2",
      q: address,
      limit: "1",
      addressdetails: "0",
    }).toString();

  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) return null;

  const rows = await response.json();
  const first = rows?.[0];

  if (!first) return null;

  const result = {
    lat: Number(first.lat),
    lng: Number(first.lon),
  };

  if (!Number.isFinite(result.lat) || !Number.isFinite(result.lng)) {
    return null;
  }

  try {
    localStorage.setItem(key, JSON.stringify(result));
  } catch {
    // ignora errori cache
  }

  return result;
}

// Icona HTML per i marker Leaflet: nessun worker, nessuna dipendenza da
// asset esterni. "active" = sede con ritiro previsto oggi (pulsa in gold),
// "idle" = sede presente in rete ma senza ritiro oggi (grigio/bianco statico).
function buildMarkerHtml(active) {
  if (active) {
    return `
      <div class="modern-map-marker modern-map-marker--active">
        <div class="modern-map-marker__pulse modern-map-marker__pulse--one"></div>
        <div class="modern-map-marker__pulse modern-map-marker__pulse--two"></div>
        <div class="modern-map-marker__core">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 6h11v9H3zM14 9h3l4 4v2h-7zM7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
              fill="currentColor"/>
          </svg>
        </div>
      </div>
    `;
  }

 return `
  <div class="modern-map-marker modern-map-marker--idle">
    <div class="modern-map-marker__core modern-map-marker__core--idle">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 6h11v9H3zM14 9h3l4 4v2h-7zM7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
          fill="currentColor"/>
      </svg>
    </div>
  </div>
`;
}

function buildPopupHtml(site) {
  const pickupText = formatWindows(site, "ritiro");
  const openingText = formatWindows(site, "apertura");
  const displayName =
    site.nome_sede || site.ragione_sociale || site.localita || "Sede di ritiro";

  return `
    <div class="map-popup">
      <div class="map-popup__eyebrow">
        ${site.isActiveToday ? "RITIRO OGGI" : "SEDE LOGISTICA"}
      </div>
      <div class="map-popup__title">${displayName}</div>
      <div class="map-popup__address">
        ${site.indirizzo || ""}
        ${site.localita ? `<br/>${site.localita}` : ""}
        ${site.provincia ? ` (${site.provincia})` : ""}
      </div>
      ${
        site.isActiveToday
          ? `<div class="map-popup__qty">
               <span>Da evadere</span>
               <strong>${formatQuantity(site.todayQuantity)}</strong>
             </div>`
          : ""
      }
      <div class="map-popup__rows">
        <div><span>Apertura</span><b>${openingText}</b></div>
        <div><span>Ritiro</span><b>${pickupText}</b></div>
        ${
          site.referente
            ? `<div><span>Referente</span><b>${site.referente}</b></div>`
            : ""
        }
        ${
          site.telefono
            ? `<div><span>Telefono</span><b>${site.telefono}</b></div>`
            : ""
        }
      </div>
    </div>
  `;
}

function ModernLogisticsMap({ sites, suppliers, selectedSite, onSelectSite }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markersRef = useRef([]);
  const [geoSites, setGeoSites] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setGeocoding(true);

      const results = [];

      for (let index = 0; index < sites.length; index += 1) {
        const site = sites[index];
        const coords = await geocodeSite(site);

        if (cancelled) return;

        if (coords) {
          const supplier = findActiveSupplierForSite(site, suppliers);

          results.push({
            ...site,
            ...coords,
            isActiveToday: Boolean(supplier),
            todaySupplier: supplier,
            todayQuantity: Number(supplier?.totale_da_evadere || 0),
          });

        }

        const hasDirectCoords =
          Number.isFinite(Number(site?.latitudine ?? site?.lat)) &&
          Number.isFinite(
            Number(site?.longitudine ?? site?.lng ?? site?.lon),
          );

        if (!hasDirectCoords && index < sites.length - 1) {
          await sleep(1100);
        }
      }

      if (!cancelled) {
        setGeoSites(results);
        setGeocoding(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sites, suppliers]);

  // Inizializzazione Leaflet: nessun worker esterno da configurare, quindi
  // niente rischi di inizializzazione silenziosamente fallita come con
  // MapLibre sotto CRA/Webpack.
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    setMapError("");

    const map = L.map(mapContainerRef.current, {
      center: [42.4, 12.6],
      zoom: 5.4,
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
    });

    const tileLayer = L.tileLayer(DARK_TILE_URL, {
      subdomains: "abcd",
      maxZoom: 19,
      attribution: DARK_TILE_ATTRIBUTION,
    });

    tileLayer.on("load", () => {
      setMapLoaded(true);
      setMapError("");
    });

    tileLayer.on("tileerror", () => {
      setMapError(
        "Impossibile caricare le tile cartografiche (rete o CDN non raggiungibile).",
      );
    });

    tileLayer.addTo(map);
    tileLayerRef.current = tileLayer;

    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.control
      .attribution({ position: "bottomleft", prefix: false })
      .addTo(map);

    const resizeMap = () => {
      try {
        map.invalidateSize();
      } catch {
        // mappa non ancora pronta
      }
    };

    const resizeTimer = window.setTimeout(resizeMap, 60);
    const resizeObserver = new ResizeObserver(resizeMap);
    resizeObserver.observe(mapContainerRef.current);

    mapRef.current = map;

    return () => {
      window.clearTimeout(resizeTimer);
      resizeObserver.disconnect();
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      setMapLoaded(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geoSites.length) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const latlngs = [];

    geoSites.forEach((site) => {
      const size = 42;

      const icon = L.divIcon({
        html: buildMarkerHtml(site.isActiveToday),
        className: "modern-map-marker-wrapper",
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
      });

      const marker = L.marker([site.lat, site.lng], { icon })
        .addTo(map)
        .bindPopup(buildPopupHtml(site), {
          className: "modern-map-popup",
          maxWidth: 320,
          closeButton: false,
          offset: [0, -6],
        });

      marker.on("click", () => onSelectSite?.(site));

      markersRef.current.push(marker);
      latlngs.push([site.lat, site.lng]);
    });

    if (latlngs.length) {
      const bounds = L.latLngBounds(latlngs);

      map.flyToBounds(bounds, {
        padding: [80, 80],
        maxZoom: 6,
        duration: 1.1,
      });
    }
  }, [geoSites, onSelectSite]);

  useEffect(() => {
    if (!selectedSite || !mapRef.current) return;

    const match = geoSites.find(
      (site) => getSiteKey(site) === getSiteKey(selectedSite),
    );

    if (!match) return;

    mapRef.current.flyTo([match.lat, match.lng], 11.5, {
      duration: 1,
    });
  }, [selectedSite, geoSites]);

  return (
    <div className="modern-map-shell">
      <div ref={mapContainerRef} className="modern-map-canvas" />

      <div className="modern-map-vignette" />
      <div className="modern-map-grid" />

      <div className="modern-map-badge">
        <Navigation size={14} />
        <span>LIVE NETWORK</span>
        <b>{geoSites.length}</b>
      </div>

      <div className="modern-map-legend">
        <span>
          <i className="legend-dot legend-dot--active" />
          Ritiro oggi
        </span>
        <span>
          <i className="legend-dot legend-dot--idle" />
          Sede network
        </span>
      </div>

      {!mapLoaded && !mapError && (
        <div className="modern-map-bootstrap">
          <LoaderCircle size={18} />
          <div>
            <strong>Caricamento mappa</strong>
            <span>Sto caricando la cartografia...</span>
          </div>
        </div>
      )}

      {mapError && (
        <div className="modern-map-error">
          <strong>Mappa non caricata</strong>
          <span>{mapError}</span>
          <small>
            Controlla la connessione di rete verso basemaps.cartocdn.com.
          </small>
        </div>
      )}

      {geocoding && mapLoaded && (
        <div className="modern-map-loading">
          <LoaderCircle size={14} />
          Geolocalizzazione sedi...
        </div>
      )}
    </div>
  );
}

function RitiriOggiModern() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [infoRow, setInfoRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showCreate, setShowCreate] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(API_URL, {
        method: "GET",
        cache: "no-store",
      });

      const text = await response.text();

      let json;
      try {
        json = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`Risposta PHP non valida: ${text.slice(0, 800)}`);
      }

      if (!response.ok || json?.success === false) {
        throw new Error(
          json?.details
            ? `${json.error}: ${json.details}`
            : json?.error || `Errore HTTP ${response.status}`,
        );
      }

      setData(json);
    } catch (err) {
      console.error("[RITIRI]", err);
      setError(err.message || "Errore caricamento ritiri.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 3200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!showCreate) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowCreate(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showCreate]);

  useEffect(() => {
    if (!infoRow) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setInfoRow(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [infoRow]);

  const suppliers = useMemo(
    () => (Array.isArray(data?.fornitori) ? data.fornitori : []),
    [data],
  );

  const sites = useMemo(() => flattenSites(data), [data]);

  const orderedSuppliers = useMemo(
    () =>
      [...suppliers].sort((a, b) => {
        const firstA = [...(a.sedi || [])].sort((x, y) =>
          pickupSortValue(x).localeCompare(pickupSortValue(y)),
        )[0];
        const firstB = [...(b.sedi || [])].sort((x, y) =>
          pickupSortValue(x).localeCompare(pickupSortValue(y)),
        )[0];

        return pickupSortValue(firstA).localeCompare(
          pickupSortValue(firstB),
        );
      }),
    [suppliers],
  );

  // Missioni attive: le SEDI da ritirare oggi, non i fornitori. Un
  // fornitore con più sedi genera più righe, una per sede.
  const orderedSites = useMemo(() => {
    const rows = [];

    orderedSuppliers.forEach((supplier) => {
      (supplier.sedi || []).forEach((site) => {
        rows.push({ site, supplier });
      });
    });

    return rows.sort((a, b) =>
      pickupSortValue(a.site).localeCompare(pickupSortValue(b.site)),
    );
  }, [orderedSuppliers]);

  const totals = data?.totali || {
    quantita_da_evadere: 0,
    fornitori: 0,
    ordini: 0,
    documenti: 0,
    articoli: 0,
  };

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const submitSite = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          codice_fornitore: Number(form.codice_fornitore),
        }),
      });

      const text = await response.text();
      const json = text ? JSON.parse(text) : {};

      if (!response.ok || json?.success === false) {
        throw new Error(
          json?.details
            ? `${json.error}: ${json.details}`
            : json?.error || "Errore salvataggio sede.",
        );
      }

      setNotice("Sede salvata correttamente.");
      setForm(EMPTY_FORM);
      setShowCreate(false);
      await loadData();
    } catch (err) {
      console.error("[RITIRI POST]", err);
      setError(err.message || "Errore salvataggio sede.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="control-shell w-100" style={{maxHeight: "100vh", overflowY: 'scroll'}}>

      <NavbarUtility />

      
      <main className="control-page">
        <header className="control-topbar">
          <div className="control-title">
            <div className="control-eyebrow">
              <span className="control-live-dot" />
              LOGISTICS CONTROL TOWER
            </div>

            <h1>
              Ritiri <strong>oggi</strong>
            </h1>
          </div>

          <div className="control-actions">
            <div className="control-date">
              <CalendarDays size={16} />
              <span>{formatLongDate(data?.data_oggi)}</span>
            </div>

            <button
              type="button"
              className="icon-button"
              onClick={loadData}
              disabled={loading}
              title="Aggiorna"
            >
              <RefreshCw className={loading ? "spin" : ""} size={17} />
            </button>
          </div>
        </header>

        <section className="control-kpis">
          <article className="kpi kpi--hero">
            <div className="kpi-icon">
              <PackageCheck size={19} />
            </div>
            <span>Quantità da evadere</span>
            <strong>{formatQuantity(totals.quantita_da_evadere)}</strong>
            <small>materiale previsto oggi</small>
          </article>

          <article className="kpi">
            <Truck size={18} />
            <span>Fornitori</span>
            <strong>{totals.fornitori || suppliers.length}</strong>
          </article>

          <article className="kpi">
            <Route size={18} />
            <span>Ordini</span>
            <strong>{totals.ordini ?? totals.documenti ?? 0}</strong>
          </article>

          <article className="kpi">
            <Building2 size={18} />
            <span>Rete sedi</span>
            <strong>{sites.length}</strong>
          </article>
        </section>

        {notice && <div className="toast toast--ok">{notice}</div>}

        {error && (
          <div className="toast toast--error">
            <span>{error}</span>
            <button type="button" onClick={() => setError("")}>
              <X size={14} />
            </button>
          </div>
        )}

        <section className="command-center">
          <ModernLogisticsMap
            sites={sites}
            suppliers={suppliers}
            selectedSite={selectedSite}
            onSelectSite={setSelectedSite}
          />

          <aside className="today-panel">
            <div className="today-panel-head">
              <div>
                <span>MISSIONI ATTIVE</span>
                <h2>Ritiri di oggi</h2>
              </div>
              <div className="today-count">{orderedSites.length}</div>
            </div>

            <div className="today-list">
              {loading && (
                <div className="panel-empty">
                  <LoaderCircle className="spin" size={18} />
                  Aggiornamento...
                </div>
              )}

              {!loading && orderedSites.length === 0 && (
                <div className="panel-empty">
                  <Truck size={20} />
                  Nessun ritiro oggi
                </div>
              )}

              {orderedSites.map(({ site, supplier }) => {
                const isSelected =
                  selectedSite && getSiteKey(selectedSite) === getSiteKey(site);

                return (
                  <div
                    className={`mission ${isSelected ? "mission--open" : ""}`}
                    key={getSiteKey(site)}
                  >
                    <div
                      className="mission-main"
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedSite(site)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedSite(site);
                        }
                      }}
                    >
                      <div className="mission-index">
                        <MapPin size={13} />
                      </div>

                      <div className="mission-copy">
                        <small>{supplier.ragione_sociale}</small>
                        <strong>{site.nome_sede || site.localita}</strong>
                        <span>
                          <Clock3 size={12} />
                          {formatWindows(site, "ritiro")}
                        </span>
                      </div>

                      <div className="mission-qty">
                        <small>Q.TÀ</small>
                        <b>{formatQuantity(supplier.totale_da_evadere)}</b>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mission-info-btn"
                      onClick={() => setInfoRow({ site, supplier })}
                      aria-label="Info da evadere"
                      title="Info da evadere"
                    >
                      <PackageCheck size={14} />
                      Info
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="today-panel-footer">
              <span>
                <i className="legend-dot legend-dot--active" />
                Live
              </span>
              <small>
                {data?.ora_server ? `Agg. ${data.ora_server}` : "Live data"}
              </small>
            </div>
          </aside>
        </section>

        {selectedSite && (
          <section className="selected-site-bar">
            <div className="selected-site-main">
              <div className="selected-site-pin">
                <MapPin size={17} />
              </div>

              <div>
                <small>SEDE SELEZIONATA</small>
                <strong>
                  {selectedSite.nome_sede ||
                    selectedSite.ragione_sociale ||
                    selectedSite.localita}
                </strong>
                <span>
                  {selectedSite.indirizzo}
                  {selectedSite.localita
                    ? ` · ${selectedSite.localita}`
                    : ""}
                  {selectedSite.provincia
                    ? ` (${selectedSite.provincia})`
                    : ""}
                </span>
              </div>
            </div>

            <div className="selected-site-meta">
              <div>
                <Clock3 size={14} />
                <span>
                  <small>Ritiro</small>
                  <b>{formatWindows(selectedSite, "ritiro")}</b>
                </span>
              </div>

              {selectedSite.telefono && (
                <div>
                  <Phone size={14} />
                  <span>
                    <small>Telefono</small>
                    <b>{selectedSite.telefono}</b>
                  </span>
                </div>
              )}

              <a
                href={mapsUrl(selectedSite)}
                target="_blank"
                rel="noreferrer"
              >
                <Navigation size={15} />
                Indicazioni
                <ExternalLink size={12} />
              </a>
            </div>

            <button
              type="button"
              className="selected-close"
              onClick={() => setSelectedSite(null)}
            >
              <X size={16} />
            </button>
          </section>
        )}


        <button
          type="button"
          className="floating-create"
          onClick={() => setShowCreate(true)}
          aria-label="Aggiungi una nuova sede di ritiro"
          title="Aggiungi una nuova sede di ritiro"
        >
          <Plus size={24} />
        </button>

        {showCreate && (
          <div
            className="create-modal-backdrop"
            onMouseDown={() => setShowCreate(false)}
          >
            <div
              className="create-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-site-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="create-modal-head">
                <div>
                  <small>ANAGRAFICA LOGISTICA</small>
                  <h2 id="create-site-title">Nuova sede di ritiro</h2>
                </div>
                <button
                  type="button"
                  className="create-modal-close"
                  onClick={() => setShowCreate(false)}
                  aria-label="Chiudi"
                >
                  <X size={17} />
                </button>
              </div>

              <form className="create-form create-form--modal" onSubmit={submitSite}>
              <div className="form-grid">
                {[
                  ["codice_fornitore", "Codice fornitore", "number"],
                  ["ragione_sociale", "Ragione sociale", "text"],
                  ["nome_sede", "Nome sede", "text"],
                  ["indirizzo", "Indirizzo", "text"],
                  ["localita", "Località", "text"],
                  ["provincia", "Provincia", "text"],
                  ["referente", "Referente", "text"],
                  ["telefono", "Telefono", "text"],
                  ["email", "Email", "email"],
                ].map(([field, label, type]) => (
                  <label key={field}>
                    <span>{label}</span>
                    <input
                      type={type}
                      value={form[field]}
                      required={[
                        "codice_fornitore",
                        "nome_sede",
                        "indirizzo",
                        "localita",
                      ].includes(field)}
                      onChange={(event) =>
                        updateField(field, event.target.value)
                      }
                    />
                  </label>
                ))}
              </div>

              <div className="time-groups">
                <div>
                  <strong>Apertura</strong>
                  <div className="time-grid">
                    {[
                      ["apertura_mattina_da", "Mattina da"],
                      ["apertura_mattina_a", "Mattina a"],
                      ["apertura_pomeriggio_da", "Pomeriggio da"],
                      ["apertura_pomeriggio_a", "Pomeriggio a"],
                    ].map(([field, label]) => (
                      <label key={field}>
                        <span>{label}</span>
                        <input
                          type="time"
                          value={form[field]}
                          onChange={(event) =>
                            updateField(field, event.target.value)
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <strong>Ritiro</strong>
                  <div className="time-grid">
                    {[
                      ["ritiro_mattina_da", "Mattina da"],
                      ["ritiro_mattina_a", "Mattina a"],
                      ["ritiro_pomeriggio_da", "Pomeriggio da"],
                      ["ritiro_pomeriggio_a", "Pomeriggio a"],
                    ].map(([field, label]) => (
                      <label key={field}>
                        <span>{label}</span>
                        <input
                          type="time"
                          value={form[field]}
                          onChange={(event) =>
                            updateField(field, event.target.value)
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <label className="notes-field">
                <span>Note</span>
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={(event) =>
                    updateField("note", event.target.value)
                  }
                />
              </label>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setForm(EMPTY_FORM)}
                >
                  Pulisci
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <LoaderCircle className="spin" size={15} />
                  ) : (
                    <Save size={15} />
                  )}
                  Salva sede
                </button>
              </div>
            </form>
            </div>
          </div>
        )}

        {infoRow && (
          <div
            className="create-modal-backdrop"
            onMouseDown={() => setInfoRow(null)}
          >
            <div
              className="create-modal info-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="info-site-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="create-modal-head">
                <div>
                  <small>DA EVADERE</small>
                  <h2 id="info-site-title">
                    {infoRow.site.nome_sede || infoRow.site.localita}
                  </h2>
                </div>
                <button
                  type="button"
                  className="create-modal-close"
                  onClick={() => setInfoRow(null)}
                  aria-label="Chiudi"
                >
                  <X size={17} />
                </button>
              </div>

              <div className="info-modal-body">
                <div className="info-modal-summary">
                  <div>
                    <small>Fornitore</small>
                    <strong>
                      {infoRow.supplier.codice_fornitore} ·{" "}
                      {infoRow.supplier.ragione_sociale}
                    </strong>
                  </div>
                  <div>
                    <small>Totale da evadere</small>
                    <strong>
                      {formatQuantity(infoRow.supplier.totale_da_evadere)}
                    </strong>
                  </div>
                  <div>
                    <small>Ritiro sede</small>
                    <strong>{formatWindows(infoRow.site, "ritiro")}</strong>
                  </div>
                </div>

                <div className="orders-table">
                  <div className="orders-row orders-row--head">
                    <span>Documento</span>
                    <span>Mappa</span>
                    <span>Articolo</span>
                    <span>Data</span>
                    <span>Q.tà</span>
                  </div>

                  {(infoRow.supplier.ordini || infoRow.supplier.righe || [])
                    .length === 0 && (
                    <div className="panel-empty">Nessun ordine disponibile</div>
                  )}

                  {(infoRow.supplier.ordini || infoRow.supplier.righe || []).map(
                    (order, index) => (
                      <div
                        className="orders-row"
                        key={`${infoRow.supplier.codice_fornitore}-${order.numero_doc}-${order.articolo}-${index}`}
                      >
                        <span>
                          <small>{order.tipo_doc}</small>
                          <b>{order.numero_doc || "—"}</b>
                        </span>
                        <span>{order.precodice || "—"}</span>
                        <span>{order.articolo || "—"}</span>
                        <span>
                          {formatDbDate(
                            order.data_prevista || order.data_conferma,
                          )}
                        </span>
                        <span className="order-qty">
                          {formatQuantity(order.qta_dev)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <style>{`
        :root {
          color-scheme: dark;
        }

        * {
          box-sizing: border-box;
        }

        .control-shell {
          min-height: 100vh;
          color: #f8f8f6;
          background:
            radial-gradient(circle at 14% -10%, rgba(235,181,27,.12), transparent 30%),
            #15171b;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        .control-page {
          width: min(1680px, calc(100% - 36px));
          margin: 0 auto;
          padding: 30px 0 70px;
        }

        .control-topbar {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 20px;
        }

        .control-eyebrow {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 9px;
          color: rgba(255,255,255,.43);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.8px;
        }

        .control-live-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: ${GOLD};
          box-shadow: 0 0 0 5px rgba(235,181,27,.1);
        }

        .control-title h1 {
          margin: 0;
          font-size: clamp(39px, 4vw, 62px);
          line-height: .95;
          font-weight: 300;
          letter-spacing: -2.8px;
        }

        .control-title h1 strong {
          color: ${GOLD};
          font-weight: 750;
        }

        .control-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .control-date,
        .icon-button {
          border: 1px solid rgba(255,255,255,.1);
          background: rgba(255,255,255,.05);
          backdrop-filter: blur(18px);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.08);
        }

        .control-date {
          display: flex;
          align-items: center;
          gap: 9px;
          min-height: 40px;
          padding: 0 13px;
          border-radius: 12px;
          color: rgba(255,255,255,.64);
          font-size: 11px;
          text-transform: capitalize;
        }

        .control-date svg {
          color: ${GOLD};
        }

        .icon-button {
          display: grid;
          place-items: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          color: ${GOLD};
          cursor: pointer;
        }

        .control-kpis {
          display: grid;
          grid-template-columns: 1.5fr repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 12px;
        }

        .kpi {
          min-height: 100px;
          padding: 15px 16px;
          border: 1px solid rgba(255,255,255,.085);
          border-radius: 18px;
          background:
            linear-gradient(145deg, rgba(255,255,255,.07), rgba(255,255,255,.025));
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.08),
            0 16px 40px rgba(0,0,0,.13);
        }

        .kpi svg {
          color: ${GOLD};
        }

        .kpi > span {
          display: block;
          margin-top: 11px;
          color: rgba(255,255,255,.37);
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .7px;
        }

        .kpi > strong {
          display: block;
          margin-top: 1px;
          font-size: 24px;
          letter-spacing: -.8px;
        }

        .kpi--hero {
          position: relative;
          overflow: hidden;
          background:
            linear-gradient(120deg, rgba(235,181,27,.13), rgba(255,255,255,.035));
          border-color: rgba(235,181,27,.18);
        }

        .kpi--hero::after {
          content: "";
          position: absolute;
          right: -22px;
          bottom: -50px;
          width: 135px;
          height: 135px;
          border-radius: 50%;
          border: 24px solid rgba(235,181,27,.035);
        }

        .kpi--hero .kpi-icon {
          display: grid;
          place-items: center;
          width: 31px;
          height: 31px;
          border-radius: 10px;
          color: #17191d;
          background: ${GOLD};
        }

        .kpi--hero small {
          position: absolute;
          right: 16px;
          bottom: 17px;
          color: rgba(255,255,255,.25);
          font-size: 8px;
        }

        .command-center {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 12px;
          height: clamp(560px, calc(100dvh - 245px), 760px);
          min-height: 560px;
          align-items: stretch;
        }

        .modern-map-shell {
          position: relative;
          min-width: 0;
          height: 100%;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.1);
          border-radius: 26px;
          background: #17191d;
          box-shadow:
            0 30px 80px rgba(0,0,0,.32),
            inset 0 1px 0 rgba(255,255,255,.08);
        }

        .modern-map-canvas {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          min-height: 520px;
          background: #17191d;
        }

        .leaflet-container {
          background: #17191d !important;
          font-family: inherit !important;
        }

        .modern-map-bootstrap,
        .modern-map-error {
          position: absolute;
          z-index: 500;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          gap: 12px;
          max-width: min(430px, calc(100% - 40px));
          padding: 14px 16px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 14px;
          color: rgba(255,255,255,.72);
          background: rgba(19,21,25,.92);
          backdrop-filter: blur(20px);
          box-shadow: 0 20px 60px rgba(0,0,0,.38);
        }

        .modern-map-bootstrap > svg {
          flex: 0 0 auto;
          color: ${GOLD};
          animation: spin .9s linear infinite;
        }

        .modern-map-bootstrap > div,
        .modern-map-error {
          flex-direction: column;
          align-items: flex-start;
        }

        .modern-map-bootstrap strong,
        .modern-map-error strong {
          color: #fff;
          font-size: 11px;
        }

        .modern-map-bootstrap span,
        .modern-map-error span {
          margin-top: 2px;
          color: rgba(255,255,255,.48);
          font-size: 8px;
          line-height: 1.45;
          overflow-wrap: anywhere;
        }

        .modern-map-error {
          border-color: rgba(255,104,104,.22);
        }

        .modern-map-error strong {
          color: #ffd0d0;
        }

        .modern-map-error small {
          color: rgba(255,255,255,.32);
          font-size: 7px;
          line-height: 1.4;
        }

        .modern-map-vignette {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(90deg, rgba(15,17,20,.16), transparent 35%),
            linear-gradient(0deg, rgba(15,17,20,.2), transparent 28%);
          z-index: 400;
        }

        .modern-map-grid {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 400;
          opacity: .055;
          background-image:
            linear-gradient(rgba(235,181,27,.45) 1px, transparent 1px),
            linear-gradient(90deg, rgba(235,181,27,.45) 1px, transparent 1px);
          background-size: 42px 42px;
          mask-image: linear-gradient(to right, #000 0%, transparent 70%);
        }

        .modern-map-badge,
        .modern-map-legend,
        .modern-map-loading {
          position: absolute;
          z-index: 450;
          display: flex;
          align-items: center;
          border: 1px solid rgba(255,255,255,.1);
          background: rgba(19,21,25,.74);
          backdrop-filter: blur(18px);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.07);
        }

        .modern-map-badge {
          top: 17px;
          left: 17px;
          gap: 8px;
          height: 36px;
          padding: 0 11px;
          border-radius: 11px;
          color: ${GOLD};
        }

        .modern-map-badge span {
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1.2px;
        }

        .modern-map-badge b {
          display: grid;
          place-items: center;
          min-width: 21px;
          height: 21px;
          padding: 0 5px;
          border-radius: 7px;
          color: #17191d;
          background: ${GOLD};
          font-size: 9px;
        }

        .modern-map-legend {
          left: 17px;
          bottom: 17px;
          gap: 16px;
          min-height: 35px;
          padding: 0 11px;
          border-radius: 10px;
        }

        .modern-map-legend span,
        .today-panel-footer span {
          display: flex;
          align-items: center;
          gap: 6px;
          color: rgba(255,255,255,.48);
          font-size: 8px;
          font-weight: 700;
        }

        .legend-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
        }

        .legend-dot--active {
          background: ${GOLD};
          box-shadow: 0 0 0 4px rgba(235,181,27,.13);
        }

        .legend-dot--idle {
          border: 1px solid rgba(255,255,255,.78);
          background: #8f949d;
        }

        .modern-map-loading {
          left: 17px;
          bottom: 61px;
          gap: 7px;
          min-height: 32px;
          padding: 0 10px;
          border-radius: 9px;
          color: rgba(255,255,255,.47);
          font-size: 8px;
        }

        .modern-map-loading svg {
          color: ${GOLD};
          animation: spin .9s linear infinite;
        }

        /* ───────────────────────── MARKER LEAFLET ───────────────────────── */

        .leaflet-div-icon.modern-map-marker-wrapper {
          background: transparent;
          border: 0;
        }

        .modern-map-marker {
          position: relative;
          width: 100%;
          height: 100%;
          display: grid;
          place-items: center;
          cursor: pointer;
        }

        .modern-map-marker--idle {
          width: 20px;
          height: 20px;
        }

        .modern-map-marker__idle-dot {
          width: 10px;
          height: 10px;
          border: 1.5px solid rgba(255,255,255,.92);
          border-radius: 50%;
          background: #8f949d;
          box-shadow:
            0 0 0 4px rgba(255,255,255,.07),
            0 4px 12px rgba(0,0,0,.35);
          transition: transform .16s ease, background .16s ease;
        }

        .modern-map-marker--idle:hover .modern-map-marker__idle-dot {
          transform: scale(1.2);
          background: #b8bcc3;
        }

        .modern-map-marker--active {
          width: 42px;
          height: 42px;
        }

        .modern-map-marker__core {
          position: relative;
          z-index: 3;
          display: grid;
          place-items: center;
          width: 28px;
          height: 28px;
          border: 2px solid #f3cf55;
          border-radius: 10px;
          color: #17191d;
          background: ${GOLD};
          transform: rotate(45deg);
          box-shadow:
            0 8px 24px rgba(0,0,0,.34),
            0 0 22px rgba(235,181,27,.2);
        }

        .modern-map-marker__core svg {
          width: 15px;
          height: 15px;
          transform: rotate(-45deg);
        }

        .modern-map-marker__pulse {
          position: absolute;
          z-index: 1;
          width: 30px;
          height: 30px;
          border: 1px solid rgba(235,181,27,.75);
          border-radius: 50%;
          animation: mapPulse 2.1s ease-out infinite;
        }

        .modern-map-marker__pulse--two {
          animation-delay: .7s;
        }

        @keyframes mapPulse {
          0% {
            transform: scale(.65);
            opacity: .85;
          }
          100% {
            transform: scale(2.15);
            opacity: 0;
          }
        }

        .today-panel {
          position: relative;
          z-index: 480;
          min-width: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.115);
          border-radius: 21px;
          background:
            linear-gradient(155deg, rgba(27,29,34,.90), rgba(18,20,23,.79));
          backdrop-filter: blur(25px) saturate(130%);
          box-shadow:
            0 24px 60px rgba(0,0,0,.28),
            inset 0 1px 0 rgba(255,255,255,.075);
        }

        .today-panel-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 17px 17px 13px;
          border-bottom: 1px solid rgba(255,255,255,.07);
        }

        .today-panel-head span,
        .section-heading span {
          color: ${GOLD};
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1.4px;
        }

        .today-panel-head h2,
        .section-heading h2 {
          margin: 3px 0 0;
          font-size: 18px;
          letter-spacing: -.4px;
        }

        .today-count {
          display: grid;
          place-items: center;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          color: #17191d;
          background: ${GOLD};
          font-size: 12px;
          font-weight: 900;
        }

        .today-list {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-gutter: stable;
          scroll-behavior: smooth;
          padding: 8px;
          scrollbar-width: thin;
          scrollbar-color: rgba(235,181,27,.28) transparent;
        }

        .today-list::-webkit-scrollbar {
          width: 6px;
        }

        .today-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .today-list::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: rgba(235,181,27,.24);
        }

        .today-list::-webkit-scrollbar-thumb:hover {
          background: rgba(235,181,27,.38);
        }

        .mission {
          display: flex;
          align-items: stretch;
          gap: 6px;
          margin-bottom: 6px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.065);
          border-radius: 14px;
          background: rgba(255,255,255,.025);
          transition: .2s ease;
        }

        .mission--open {
          border-color: rgba(235,181,27,.21);
          background: rgba(235,181,27,.035);
        }

        .mission-main {
          flex: 1;
          min-width: 0;
          display: grid;
          grid-template-columns: 27px minmax(0,1fr) auto;
          align-items: center;
          gap: 9px;
          padding: 11px 10px;
          border: 0;
          color: inherit;
          background: transparent;
          text-align: left;
          cursor: pointer;
        }

        .mission-index {
          display: grid;
          place-items: center;
          width: 27px;
          height: 27px;
          border-radius: 9px;
          color: #17191d;
          background: ${GOLD};
          font-size: 9px;
          font-weight: 900;
        }

        .mission-copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .mission-copy small {
          color: rgba(255,255,255,.28);
          font-size: 7px;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mission-copy strong {
          margin-top: 1px;
          overflow: hidden;
          font-size: 10px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mission-copy span {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 4px;
          color: ${GOLD};
          font-size: 8px;
          font-weight: 700;
        }

        .mission-qty {
          min-width: 43px;
          text-align: right;
        }

        .mission-qty small {
          display: block;
          color: rgba(255,255,255,.24);
          font-size: 6px;
          font-weight: 800;
        }

        .mission-qty b {
          display: block;
          color: ${GOLD};
          font-size: 13px;
        }

        .mission-info-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          width: 52px;
          flex: 0 0 52px;
          border: 0;
          border-left: 1px solid rgba(255,255,255,.06);
          color: ${GOLD};
          background: rgba(235,181,27,.045);
          font-size: 7px;
          font-weight: 850;
          letter-spacing: .3px;
          cursor: pointer;
          transition: background .15s ease;
        }

        .mission-info-btn:hover {
          background: rgba(235,181,27,.11);
        }

        .today-panel-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 39px;
          padding: 0 13px;
          border-top: 1px solid rgba(255,255,255,.07);
          color: rgba(255,255,255,.3);
          font-size: 7px;
        }

        .panel-empty {
          min-height: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: rgba(255,255,255,.37);
          font-size: 9px;
        }

        .selected-site-bar {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0,1fr) auto auto;
          align-items: center;
          gap: 20px;
          margin-top: 10px;
          padding: 12px 14px;
          border: 1px solid rgba(235,181,27,.17);
          border-radius: 16px;
          background:
            linear-gradient(120deg, rgba(235,181,27,.075), rgba(255,255,255,.03));
        }

        .selected-site-main {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .selected-site-pin {
          display: grid;
          place-items: center;
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          border-radius: 11px;
          color: #17191d;
          background: ${GOLD};
        }

        .selected-site-main > div:last-child {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .selected-site-main small,
        .selected-site-meta small {
          color: rgba(255,255,255,.28);
          font-size: 7px;
          font-weight: 800;
        }

        .selected-site-main strong {
          margin-top: 1px;
          font-size: 11px;
        }

        .selected-site-main span {
          margin-top: 2px;
          overflow: hidden;
          color: rgba(255,255,255,.4);
          font-size: 8px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .selected-site-meta {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .selected-site-meta > div,
        .selected-site-meta > a {
          display: flex;
          align-items: center;
          gap: 7px;
          color: rgba(255,255,255,.58);
          text-decoration: none;
        }

        .selected-site-meta svg {
          color: ${GOLD};
        }

        .selected-site-meta span {
          display: flex;
          flex-direction: column;
        }

        .selected-site-meta b {
          margin-top: 1px;
          font-size: 8px;
          font-weight: 700;
        }

        .selected-site-meta > a {
          min-height: 34px;
          padding: 0 10px;
          border: 1px solid rgba(235,181,27,.18);
          border-radius: 10px;
          color: ${GOLD};
          background: rgba(235,181,27,.05);
          font-size: 8px;
          font-weight: 800;
        }

        .selected-close {
          display: grid;
          place-items: center;
          width: 31px;
          height: 31px;
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 9px;
          color: rgba(255,255,255,.45);
          background: rgba(255,255,255,.035);
          cursor: pointer;
        }

        .detail-section {
          margin-top: 28px;
        }

        .section-heading {
          margin-bottom: 12px;
        }

        .supplier-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0,1fr));
          gap: 10px;
        }

        .supplier-card {
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.085);
          border-radius: 18px;
          background:
            linear-gradient(145deg, rgba(255,255,255,.055), rgba(255,255,255,.022));
        }

        .supplier-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 13px 14px;
          border-bottom: 1px solid rgba(255,255,255,.06);
        }

        .supplier-card-head > div:first-child {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .supplier-card-head small,
        .supplier-total span {
          color: rgba(255,255,255,.28);
          font-size: 7px;
          font-weight: 800;
        }

        .supplier-card-head strong {
          margin-top: 2px;
          font-size: 11px;
        }

        .supplier-total {
          text-align: right;
        }

        .supplier-total b {
          display: block;
          color: ${GOLD};
          font-size: 16px;
        }

        .supplier-sites {
          display: flex;
          gap: 5px;
          flex-wrap: wrap;
          padding: 9px 12px;
          border-bottom: 1px solid rgba(255,255,255,.05);
        }

        .supplier-sites button {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 28px;
          padding: 0 8px;
          border: 1px solid rgba(235,181,27,.12);
          border-radius: 9px;
          color: rgba(255,255,255,.55);
          background: rgba(235,181,27,.035);
          cursor: pointer;
        }

        .supplier-sites svg {
          color: ${GOLD};
        }

        .supplier-sites span {
          font-size: 8px;
          font-weight: 750;
        }

        .supplier-sites small {
          color: ${GOLD};
          font-size: 7px;
        }

        .orders-table {
          padding: 5px 8px 8px;
        }

        .orders-row {
          display: grid;
          grid-template-columns: 1.05fr .7fr 1.2fr .65fr .5fr;
          align-items: center;
          min-height: 37px;
          padding: 0 7px;
          border-bottom: 1px solid rgba(255,255,255,.045);
          color: rgba(255,255,255,.58);
          font-size: 8px;
        }

        .orders-row:last-child {
          border-bottom: 0;
        }

        .orders-row--head {
          min-height: 30px;
          color: rgba(255,255,255,.24);
          font-size: 7px;
          font-weight: 800;
          text-transform: uppercase;
        }

        .orders-row > span:first-child {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .orders-row > span:first-child small {
          padding: 2px 4px;
          border-radius: 4px;
          color: ${GOLD};
          background: rgba(235,181,27,.06);
          font-size: 6px;
          font-weight: 850;
        }

        .order-qty {
          color: ${GOLD};
          font-size: 10px;
          font-weight: 850;
          text-align: right;
        }

        .floating-create {
          position: fixed;
          z-index: 9000;
          right: 24px;
          bottom: 24px;
          display: grid;
          place-items: center;
          width: 56px;
          height: 56px;
          padding: 0;
          border: 1px solid rgba(243,207,85,.45);
          border-radius: 18px;
          color: #17191d;
          background: ${GOLD};
          box-shadow:
            0 18px 42px rgba(0,0,0,.38),
            0 0 0 5px rgba(235,181,27,.09);
          cursor: pointer;
          transition: transform .18s ease, box-shadow .18s ease;
        }

        .floating-create:hover {
          transform: translateY(-2px) scale(1.03);
          box-shadow:
            0 22px 48px rgba(0,0,0,.42),
            0 0 0 7px rgba(235,181,27,.1);
        }

        .create-modal-backdrop {
          position: fixed;
          z-index: 12000;
          inset: 0;
          display: grid;
          place-items: center;
          padding: 24px;
          background: rgba(9,10,12,.72);
          backdrop-filter: blur(10px);
        }

        .create-modal {
          width: min(920px, 100%);
          max-height: min(88dvh, 820px);
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.115);
          border-radius: 22px;
          background:
            linear-gradient(150deg, rgba(31,33,38,.98), rgba(18,20,23,.98));
          box-shadow:
            0 30px 90px rgba(0,0,0,.55),
            inset 0 1px 0 rgba(255,255,255,.07);
        }

        .create-modal.info-modal {
          width: min(720px, 100%);
        }

        .create-modal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          min-height: 68px;
          padding: 0 18px;
          border-bottom: 1px solid rgba(255,255,255,.07);
        }

        .create-modal-head > div {
          display: flex;
          flex-direction: column;
        }

        .create-modal-head small {
          color: ${GOLD};
          font-size: 7px;
          font-weight: 850;
          letter-spacing: 1.3px;
        }

        .create-modal-head h2 {
          margin: 3px 0 0;
          font-size: 18px;
          letter-spacing: -.45px;
        }

        .create-modal-close {
          display: grid;
          place-items: center;
          width: 34px;
          height: 34px;
          padding: 0;
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 10px;
          color: rgba(255,255,255,.55);
          background: rgba(255,255,255,.04);
          cursor: pointer;
        }

        .info-modal-body {
          max-height: calc(min(88dvh, 820px) - 68px);
          overflow-y: auto;
          overscroll-behavior: contain;
          padding: 15px;
          scrollbar-width: thin;
          scrollbar-color: rgba(235,181,27,.3) transparent;
        }

        .info-modal-body::-webkit-scrollbar {
          width: 6px;
        }

        .info-modal-body::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: rgba(235,181,27,.28);
        }

        .info-modal-summary {
          display: grid;
          grid-template-columns: repeat(3, minmax(0,1fr));
          gap: 8px;
          margin-bottom: 12px;
        }

        .info-modal-summary > div {
          padding: 11px;
          border: 1px solid rgba(255,255,255,.07);
          border-radius: 12px;
          background: rgba(255,255,255,.025);
        }

        .info-modal-summary small {
          display: block;
          color: rgba(255,255,255,.32);
          font-size: 7px;
          font-weight: 800;
          text-transform: uppercase;
        }

        .info-modal-summary strong {
          display: block;
          margin-top: 4px;
          color: ${GOLD};
          font-size: 12px;
          font-weight: 800;
        }

        .create-form {
          padding: 15px;
          border-top: 1px solid rgba(255,255,255,.06);
        }

        .create-form--modal {
          max-height: calc(min(88dvh, 820px) - 68px);
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-width: thin;
          scrollbar-color: rgba(235,181,27,.3) transparent;
        }

        .create-form--modal::-webkit-scrollbar {
          width: 6px;
        }

        .create-form--modal::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: rgba(235,181,27,.28);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(3,minmax(0,1fr));
          gap: 8px;
        }

        .form-grid label,
        .time-grid label,
        .notes-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .form-grid label > span,
        .time-grid label > span,
        .notes-field > span {
          color: rgba(255,255,255,.38);
          font-size: 8px;
          font-weight: 700;
        }

        .form-grid input,
        .time-grid input,
        .notes-field textarea {
          width: 100%;
          min-height: 39px;
          padding: 0 10px;
          border: 1px solid rgba(255,255,255,.085);
          border-radius: 10px;
          outline: none;
          color: #fff;
          background: rgba(255,255,255,.035);
          font: inherit;
          font-size: 9px;
        }

        .notes-field textarea {
          padding-top: 9px;
          resize: vertical;
        }

        .form-grid input:focus,
        .time-grid input:focus,
        .notes-field textarea:focus {
          border-color: rgba(235,181,27,.33);
        }

        .time-groups {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: 10px;
        }

        .time-groups > div {
          padding: 11px;
          border: 1px solid rgba(255,255,255,.06);
          border-radius: 12px;
          background: rgba(255,255,255,.018);
        }

        .time-groups > div > strong {
          display: block;
          margin-bottom: 9px;
          color: ${GOLD};
          font-size: 8px;
          text-transform: uppercase;
        }

        .time-grid {
          display: grid;
          grid-template-columns: repeat(4,minmax(0,1fr));
          gap: 7px;
        }

        .notes-field {
          margin-top: 10px;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 7px;
          margin-top: 11px;
        }

        .btn-primary,
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 36px;
          padding: 0 13px;
          border-radius: 10px;
          font-size: 8px;
          font-weight: 800;
          cursor: pointer;
        }

        .btn-secondary {
          border: 1px solid rgba(255,255,255,.09);
          color: rgba(255,255,255,.55);
          background: rgba(255,255,255,.035);
        }

        .btn-primary {
          border: 1px solid rgba(235,181,27,.2);
          color: #17191d;
          background: ${GOLD};
        }

        .toast {
          position: fixed;
          z-index: 10000;
          top: 18px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 10px;
          max-width: min(620px, calc(100% - 30px));
          padding: 10px 13px;
          border-radius: 11px;
          backdrop-filter: blur(20px);
          box-shadow: 0 18px 55px rgba(0,0,0,.3);
          font-size: 9px;
        }

        .toast--ok {
          border: 1px solid rgba(235,181,27,.22);
          color: #ffe493;
          background: rgba(37,34,21,.9);
        }

        .toast--error {
          border: 1px solid rgba(255,100,100,.2);
          color: #ffd5d5;
          background: rgba(45,25,27,.9);
        }

        .toast button {
          display: grid;
          place-items: center;
          padding: 0;
          border: 0;
          color: inherit;
          background: transparent;
          cursor: pointer;
        }

        .spin {
          animation: spin .9s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* ───────────────────────── LEAFLET ───────────────────────── */

        .leaflet-control-zoom.leaflet-bar {
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.1) !important;
          border-radius: 10px !important;
          background: rgba(20,22,26,.74) !important;
          backdrop-filter: blur(16px);
          box-shadow: none !important;
        }

        .leaflet-control-zoom a {
          color: ${GOLD} !important;
          background: transparent !important;
          border: 0 !important;
        }

        .leaflet-control-zoom a:hover {
          background: rgba(235,181,27,.12) !important;
        }

        .leaflet-control-zoom-in {
          border-bottom: 1px solid rgba(255,255,255,.08) !important;
        }

        .leaflet-control-attribution {
          padding: 1px 7px !important;
          border-radius: 8px !important;
          color: rgba(255,255,255,.35) !important;
          background: rgba(20,22,26,.55) !important;
          font-size: 7px !important;
        }

        .leaflet-control-attribution a {
          color: rgba(235,181,27,.75) !important;
        }

        .leaflet-popup-content-wrapper {
          padding: 0 !important;
          overflow: hidden;
          border: 1px solid rgba(235,181,27,.16);
          border-radius: 14px !important;
          color: #fff;
          background:
            linear-gradient(145deg, rgba(31,33,38,.97), rgba(19,21,24,.96)) !important;
          box-shadow: 0 20px 55px rgba(0,0,0,.35) !important;
        }

        .leaflet-popup-content {
          margin: 0 !important;
          width: auto !important;
        }

        .leaflet-popup-tip {
          background: #1d1f24 !important;
          box-shadow: none !important;
        }

        .map-popup {
          min-width: 230px;
          padding: 13px;
        }

        .map-popup__eyebrow {
          color: ${GOLD};
          font-size: 7px;
          font-weight: 900;
          letter-spacing: 1.25px;
        }

        .map-popup__title {
          margin-top: 3px;
          font-size: 13px;
          font-weight: 800;
        }

        .map-popup__address {
          margin-top: 5px;
          color: rgba(255,255,255,.4);
          font-size: 8px;
          line-height: 1.45;
        }

        .map-popup__qty {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          margin-top: 10px;
          padding: 9px;
          border: 1px solid rgba(235,181,27,.13);
          border-radius: 10px;
          background: rgba(235,181,27,.045);
        }

        .map-popup__qty span {
          color: rgba(255,255,255,.35);
          font-size: 7px;
          font-weight: 700;
        }

        .map-popup__qty strong {
          color: ${GOLD};
          font-size: 20px;
        }

        .map-popup__rows {
          margin-top: 8px;
        }

        .map-popup__rows > div {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 5px 0;
          border-top: 1px solid rgba(255,255,255,.055);
          font-size: 7px;
        }

        .map-popup__rows span {
          color: rgba(255,255,255,.3);
        }

        .map-popup__rows b {
          max-width: 150px;
          color: rgba(255,255,255,.72);
          font-weight: 700;
          text-align: right;
        }

        @media (max-width: 1050px) {
          .control-kpis {
            grid-template-columns: repeat(2,minmax(0,1fr));
          }

          .command-center {
            grid-template-columns: 1fr;
            height: auto;
            min-height: 0;
          }

          .modern-map-shell {
            height: 520px;
          }

          .today-panel {
            height: 360px;
          }

          .supplier-grid {
            grid-template-columns: 1fr;
          }

          .selected-site-bar {
            grid-template-columns: 1fr auto;
          }

          .selected-site-meta {
            grid-column: 1 / -1;
            padding-left: 44px;
          }

          .form-grid {
            grid-template-columns: repeat(2,minmax(0,1fr));
          }

          .time-groups {
            grid-template-columns: 1fr;
          }

          .info-modal-summary {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 680px) {
          .control-page {
            width: min(100% - 18px,1680px);
            padding-top: 20px;
          }

          .control-topbar {
            align-items: flex-start;
            flex-direction: column;
          }

          .control-actions {
            width: 100%;
          }

          .control-date {
            flex: 1;
          }

          .control-kpis {
            grid-template-columns: 1fr 1fr;
          }

          .command-center {
            min-height: 0;
          }

          .modern-map-shell {
            height: 430px;
            border-radius: 19px;
          }

          .modern-map-canvas {
            min-height: 0;
          }

          .today-panel {
            height: 330px;
          }

          .modern-map-badge {
            top: 10px;
            left: 10px;
          }

          .modern-map-legend {
            display: none;
          }

          .selected-site-bar {
            grid-template-columns: 1fr auto;
          }

          .selected-site-meta {
            flex-wrap: wrap;
            padding-left: 0;
          }

          .orders-table {
            overflow-x: auto;
          }

          .orders-row {
            min-width: 620px;
          }

          .form-grid,
          .time-grid {
            grid-template-columns: 1fr 1fr;
          }

          .time-groups {
            grid-template-columns: 1fr;
          }

          .floating-create {
            right: 14px;
            bottom: 14px;
            width: 52px;
            height: 52px;
            border-radius: 16px;
          }

          .create-modal-backdrop {
            padding: 10px;
          }

          .create-modal {
            max-height: 94dvh;
            border-radius: 18px;
          }

          .create-form--modal,
          .info-modal-body {
            max-height: calc(94dvh - 68px);
          }

          .info-modal-summary {
            grid-template-columns: 1fr;
          }

          .mission-info-btn {
            width: 44px;
            flex: 0 0 44px;
          }
        }

        .modern-map-marker--idle {
  width: 42px;
  height: 42px;
}

.modern-map-marker__core--idle {
  border-color: rgba(255,255,255,.82);
  color: #24272c;
  background: #9da2aa;
  box-shadow:
    0 8px 24px rgba(0,0,0,.30),
    0 0 0 3px rgba(255,255,255,.06);
}

.modern-map-marker--idle:hover .modern-map-marker__core--idle {
  background: #b7bbc1;
}
      `}</style>
    </div>
  );
}

export default RitiriOggiModern;